import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { api, errorMessage, type Schemas, unwrap } from "@/api/client";
import { useMe } from "@/auth";
import Icon from "@/components/icon";
import { List, ListHeader, ListTitle } from "@/components/list";
import Loader from "@/components/loader";
import PageHeader from "@/components/page-header";
import StatusTag from "@/components/status-tag";
import Text from "@/components/text";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const USERS_KEY = ["users"] as const;

export function UsersPage() {
  const users = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => unwrap(api.GET("/api/users")),
  });

  return (
    <>
      <PageHeader title="Users" description="People who can sign in and start runs with their own seller key." />
      <CreateUser />
      <List>
        {users.isPending && (
          <div className="grid place-items-center py-16 text-muted-foreground">
            <Loader />
          </div>
        )}
        {users.isError && <Text.Danger className="px-(--list-inset) py-4">{errorMessage(users.error)}</Text.Danger>}
        {users.data && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>API key</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.data.map((u) => (
                <UserRow key={u.id} user={u} />
              ))}
            </TableBody>
          </Table>
        )}
      </List>
    </>
  );
}

function CreateUser() {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const create = useMutation({
    mutationFn: () => unwrap(api.POST("/api/users", { body: { username, password, is_admin: isAdmin } })),
    onSuccess: (u) => {
      setUsername("");
      setPassword("");
      setIsAdmin(false);
      toast.success(`Added ${u.username}`);
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <List>
      <ListHeader>
        <ListTitle>Add a user</ListTitle>
      </ListHeader>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3 px-(--list-inset) pt-1 pb-4">
        <div className="flex w-full flex-col gap-2 sm:w-56">
          <Label htmlFor="new-username" className="font-normal">
            Username
          </Label>
          <Input
            id="new-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="off"
          />
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-56">
          <Label htmlFor="new-password" className="font-normal">
            Password
          </Label>
          <Input
            id="new-password"
            type="password"
            value={password}
            minLength={8}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>
        <Label className="h-(--control-height) cursor-pointer font-normal">
          <Checkbox checked={isAdmin} onCheckedChange={(v) => setIsAdmin(v === true)} />
          Admin
        </Label>
        <Button type="submit" loading={create.isPending}>
          Add user
        </Button>
      </form>
    </List>
  );
}

function UserRow({ user }: { user: Schemas["UserOut"] }) {
  const me = useMe().data;
  const queryClient = useQueryClient();
  const [resetting, setResetting] = useState(false);
  const [password, setPassword] = useState("");
  const isSelf = me?.id === user.id;

  const update = useMutation({
    mutationFn: (body: Schemas["UpdateUserRequest"]) =>
      unwrap(api.PATCH("/api/users/{user_id}", { params: { path: { user_id: user.id } }, body })),
    onSuccess: (_u, body) => {
      setResetting(false);
      setPassword("");
      toast.success(body.password ? "Password reset" : "Saved");
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-full bg-primary/10 text-sm font-semibold text-primary uppercase">
            {user.username.slice(0, 1)}
          </span>
          <span className="font-medium">{user.username}</span>
          {isSelf && <Text.Hint size="caption">you</Text.Hint>}
        </div>
      </TableCell>
      <TableCell>{user.is_admin ? "Admin" : "Operator"}</TableCell>
      <TableCell>
        <StatusTag state={user.is_active ? "active" : "inactive"} />
      </TableCell>
      <TableCell>{user.has_api_key ? "Saved" : <Text.Hint>None</Text.Hint>}</TableCell>
      <TableCell className="text-muted-foreground">{new Date(user.created_at).toLocaleDateString()}</TableCell>
      <TableCell>
        {resetting ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate({ password });
            }}
          >
            <Input
              type="password"
              aria-label={`New password for ${user.username}`}
              className="h-(--control-height-sm) w-44"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              autoComplete="new-password"
              required
              autoFocus
            />
            <Button type="submit" size="sm" loading={update.isPending}>
              Set
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setResetting(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8" aria-label={`Actions for ${user.username}`}>
                <Icon name="more" size={20} className="text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onSelect={() => setResetting(true)}>Reset password</DropdownMenuItem>
              {!isSelf && (
                <>
                  <DropdownMenuItem onSelect={() => update.mutate({ is_admin: !user.is_admin })}>
                    {user.is_admin ? "Make operator" : "Make admin"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant={user.is_active ? "destructive" : "default"}
                    onSelect={() => update.mutate({ is_active: !user.is_active })}
                  >
                    {user.is_active ? "Deactivate" : "Activate"}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}
